"use client";

import type { DiscoveryBookableCard } from "@/actions/venue-discovery";
import { SeeMoreCard, type SeeMoreChip } from "@/components/ui/see-more-card";
import { useRailScrollRestore } from "@/hooks/use-rail-scroll-restore";
import { cn } from "@/lib/utils";
import { BookableSlotCard } from "./bookable-slot-card.client";

interface Props {
  cards: DiscoveryBookableCard[];
  /** Section heading. Omit to render the cards bare. */
  title?: string;
  /** Small right-aligned note beside the heading. */
  caption?: string;
  /**
   * `carousel` is the For You rail, at the same widths and gap as the upcoming
   * events rail above it. `grid` is the browse layout for a tab that owns the
   * screen.
   */
  layout?: "grid" | "carousel";
  /** Cards the full list holds beyond these. >0 ends the rail in a tile. */
  moreCount?: number;
  /** Where that tile goes. Required for it to render. */
  moreHref?: string;
  /** Posters from the hidden cards, for the tile's collage. */
  morePreviewUrls?: string[];
  /** Chips about the hidden cards ("12 today", "Yoga"), for the tile. */
  moreChips?: SeeMoreChip[];
  className?: string;
}

/**
 * "What can I actually turn up to" — the bookable times themselves.
 *
 * A list of venues buries its own inventory: the open 6 PM court is three taps
 * down, behind a venue page and a service page, so nobody finds it. One card
 * per bookable experience, each ending in tappable start times, is what makes
 * that inventory browsable — and it puts this section in the same visual
 * language as the event rail it sits beside, which is the other half of why
 * nobody was using it.
 */
export function BookableNowCards({
  cards,
  title,
  caption,
  layout = "grid",
  moreCount = 0,
  moreHref,
  morePreviewUrls,
  moreChips,
  className,
}: Props) {
  // Only the carousel scrolls sideways; the grid layout is a browse page.
  const railRef = useRailScrollRestore<HTMLUListElement>(
    layout === "carousel" ? "hiives-open-to-book" : undefined,
  );

  if (cards.length === 0) return null;

  const header =
    title || caption ? (
      <div className="flex items-baseline justify-between gap-3 px-1 pb-2">
        {title ? (
          <h2 className="text-base font-semibold text-text-primary dark:text-white">
            {title}
          </h2>
        ) : (
          <span />
        )}
        {caption ? (
          <span className="text-xs text-text-tertiary dark:text-white/42">
            {caption}
          </span>
        ) : null}
      </div>
    ) : null;

  if (layout === "carousel") {
    return (
      <section className={className}>
        {header}
        <ul
          ref={railRef}
          className="scrollbar-hide flex gap-3 overflow-x-auto pb-4"
          aria-label="Find your next class"
        >
          {cards.map((card, index) => (
            <li
              key={cardKey(card)}
              className={cn(
                "w-[65%] min-w-[65%] shrink-0 md:w-[38%] md:min-w-[38%] lg:w-[30%] lg:min-w-[30%]",
                index === 0 && "ml-4",
              )}
            >
              <BookableSlotCard
                card={card}
                size="large"
                priorityMedia={index === 0}
              />
            </li>
          ))}
          {moreHref && moreCount > 0 ? (
            // A card's own slot: same width, and the row stretches it to the
            // same height, so the rail ends on a card rather than a gap.
            <li className="mr-4 w-[65%] min-w-[65%] shrink-0 md:w-[38%] md:min-w-[38%] lg:w-[30%] lg:min-w-[30%]">
              <SeeMoreCard
                href={moreHref}
                count={moreCount}
                noun={
                  moreCount === 1
                    ? "experience available"
                    : "experiences available"
                }
                previewImageUrls={morePreviewUrls}
                chips={moreChips}
                chipRows={1}
              />
            </li>
          ) : null}
        </ul>
      </section>
    );
  }

  return (
    <section className={className}>
      {header}
      <div className="grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-4">
        {cards.map((card) => (
          <BookableSlotCard
            key={cardKey(card)}
            card={card}
            // Browse grid: many tiles are on screen at once, so audio
            // ownership is meaningless here and a per-tile volume control is
            // noise. Silent autoplay only.
            forceMutedMedia
          />
        ))}
      </div>
    </section>
  );
}

/**
 * An experience appears once per activity group, and the same experience can
 * legitimately sit in two groups, so the activity has to be part of the key.
 */
const cardKey = (card: DiscoveryBookableCard) =>
  `${card.activityId}-${card.experienceId}`;
